export type PbiReviewStatus = 'Keep' | 'Retire' | 'Undecided';
export type PbiUsageWindow = '30' | '90' | '180' | 'all';
export type PbiUsageFilterUsage = 'used' | 'unused';
export type PbiSortField = 'views' | 'distinctUsers' | 'lastViewedAt' | 'daysSinceLastView' | 'name' | 'workspaceName';
export type SortOrder = 'asc' | 'desc';

export interface PbiUsageFilters {
    workspaceId?: string;
    search?: string;
    reviewStatus?: PbiReviewStatus;
    usage?: PbiUsageFilterUsage;
    includePersonal?: boolean;
    includeDeleted?: boolean;
}

export interface PbiUsageStats {
    totalReports: number;
    activeReports: number;
    unusedReports: number;
    viewsInWindow: number;
    distinctUsersInWindow: number;
    reviewKeep: number;
    reviewRetire: number;
    reviewUndecided: number;
    lastSuccessfulRunAt: string | null;
    lastEventAt: string | null;
    collectionSince: string | null;
}

export interface PbiReportRow {
    reportId: string;
    name: string;
    reportType: string | null;
    workspaceId: string | null;
    workspaceName: string | null;
    workspaceType: string | null;
    isDeleted: boolean;
    firstSeenAt: string | null;
    views: number;
    distinctUsers: number;
    lastViewedAt: string | null;
    daysSinceLastView: number | null;
    reviewStatus: PbiReviewStatus | null;
    reviewNote: string | null;
    reviewBy: string | null;
    reviewAt: string | null;
    /** 8 weekly view counts, oldest first (index 7 = current week). */
    trend: number[];
}

export interface PbiWorkspaceOption {
    workspaceId: string;
    name: string;
    type: string | null;
    reportCount: number;
}

export interface PbiUsageFilterOptions {
    workspaces: PbiWorkspaceOption[];
}

export interface PbiReviewInfo {
    status: PbiReviewStatus | null;
    note: string | null;
    by: string | null;
    at: string | null;
}

export interface PbiReportDetail {
    reportId: string;
    name: string;
    reportType: string | null;
    workspaceId: string | null;
    workspaceName: string | null;
    workspaceType: string | null;
    datasetId: string | null;
    webUrl: string | null;
    createdDateTime: string | null;
    modifiedDateTime: string | null;
    modifiedBy: string | null;
    firstSeenAt: string | null;
    lastSeenAt: string | null;
    isDeleted: boolean;
    review: PbiReviewInfo;
    totals: {
        views30: number;
        views90: number;
        viewsAllTime: number;
        usersAllTime: number;
        lastViewedAt: string | null;
    };
    dailySeries: Array<{ day: string; views: number; users: number }>;
    topUsers: Array<{ userId: string; views: number; lastViewedAt: string | null }>;
    recentEvents: Array<{
        creationTime: string;
        activity: string;
        userId: string | null;
        distributionMethod: string | null;
        consumptionMethod: string | null;
        appName: string | null;
    }>;
    reviewHistory: Array<{
        oldStatus: PbiReviewStatus | null;
        newStatus: PbiReviewStatus;
        statusNote: string | null;
        actorEmail: string;
        createdAt: string;
    }>;
}

export interface PbiReviewSetResult {
    reportId: string;
    reviewStatus: PbiReviewStatus;
    reviewNote: string | null;
    reviewBy: string;
    reviewAt: string | null;
}

/** Deep link into the Power BI service; personal workspaces have no groups/{id} URL. */
export function powerBiReportUrl(row: { workspaceId: string | null; reportId: string }): string | null {
    if (!row.workspaceId) return null;
    return `https://app.powerbi.com/groups/${row.workspaceId}/reports/${row.reportId}`;
}
